import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { systemService } from '@/services/admin/systemService'

const buildSystemConfigKey = (scope: string, env: 'mainnet' | 'testnet', extra?: unknown) => {
  if (extra === undefined) {
    return ['system-config', scope, env]
  }
  return ['system-config', scope, env, extra]
}

export function useRuntimeConfigCurrent(options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('runtime-config', currentEnv),
    queryFn: () => systemService.getRuntimeConfigCurrent(currentEnv),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  })
}

export function useUpdateRuntimeConfigCurrent() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: { configJson: Record<string, unknown>; expectedUpdatedAt: string }) =>
      systemService.updateRuntimeConfigCurrent(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('runtime-config', currentEnv) })
    },
  })
}

export function useRefreshRuntimeConfigCache() {
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => systemService.refreshRuntimeConfigCache(currentEnv),
  })
}

export function useSystemRpcEndpoints(params?: { chainCode?: string }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('rpc-endpoints', currentEnv, params ?? null),
    queryFn: () => systemService.getSystemRpcEndpoints(params, currentEnv),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  })
}

export function useUpdateSystemRpcEndpoint() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: {
        urlTemplate?: string
        provider?: string
        priority?: number
        isActive?: boolean
      }
    }) => systemService.updateSystemRpcEndpoint(id, payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('rpc-endpoints', currentEnv) })
    },
  })
}

export function useRefreshSystemRpcEndpointsCache() {
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => systemService.refreshSystemRpcEndpointsCache(currentEnv),
  })
}

export function useSystemPlatformWallets() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('platform-wallets', currentEnv),
    queryFn: () => systemService.getSystemPlatformWallets(currentEnv),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  })
}

export function useUpdateSystemPlatformWallet() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: {
        address?: string
        isActive?: boolean
        notes?: string | null
      }
    }) => systemService.updateSystemPlatformWallet(id, payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('platform-wallets', currentEnv) })
    },
  })
}

export function useRefreshSystemPlatformWalletsCache() {
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => systemService.refreshSystemPlatformWalletsCache(currentEnv),
  })
}

export function useSystemChains() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('chains', currentEnv),
    queryFn: () => systemService.getSystemChains(currentEnv),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  })
}

export function useUpdateSystemChain() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: {
        name?: string
        scanBaseUrl?: string
        confirmationBlocks?: number
        finalityOffsetMainnet?: number | null
        finalityOffsetTestnet?: number | null
        orderAmountHardFloorUsd?: number
        isActive?: boolean
        allowNewOrders?: boolean
      }
    }) => systemService.updateSystemChain(id, payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('chains', currentEnv) })
    },
  })
}

export function useRefreshSystemChainsCache() {
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => systemService.refreshSystemChainsCache(currentEnv),
  })
}

export function useSystemTokens(params?: { chainId?: string; symbol?: string }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('tokens', currentEnv, params ?? null),
    queryFn: () => systemService.getSystemTokens(params, currentEnv),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  })
}

export function useUpdateSystemToken() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: {
        name?: string
        contractAddress?: string
        decimals?: number
        isActive?: boolean
        allowNewOrders?: boolean
      }
    }) => systemService.updateSystemToken(id, payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('tokens', currentEnv) })
    },
  })
}

export function useRefreshSystemTokensCache() {
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => systemService.refreshSystemTokensCache(currentEnv),
  })
}

export function useOrderFeePolicies() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('order-fee-policies', currentEnv),
    queryFn: () => systemService.getOrderFeePolicies(currentEnv),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  })
}

export function useOrderFeePolicyDetail(policyId?: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('order-fee-policy-detail', currentEnv, policyId ?? null),
    queryFn: () => (
      policyId
        ? systemService.getOrderFeePolicyDetail(policyId, currentEnv)
        : Promise.resolve(null)
    ),
    enabled: Boolean(policyId),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  })
}

export function useCreateOrderFeePolicy() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: Parameters<typeof systemService.createOrderFeePolicy>[0]) =>
      systemService.createOrderFeePolicy(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('order-fee-policies', currentEnv) })
    },
  })
}

export function useRefreshOrderFeePolicyCache() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => systemService.refreshOrderFeePolicyCache(currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('order-fee-policies', currentEnv) })
    },
  })
}

export function useUpdateOrderFeePolicy() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: { policyId: string; updates: Parameters<typeof systemService.updateOrderFeePolicy>[1] }) =>
      systemService.updateOrderFeePolicy(payload.policyId, payload.updates, currentEnv),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: buildSystemConfigKey('order-fee-policies', currentEnv) })
      queryClient.invalidateQueries({
        queryKey: buildSystemConfigKey('order-fee-policy-detail', currentEnv, variables.policyId),
      })
    },
  })
}

export function useSubscriptionPlans(options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: buildSystemConfigKey('subscription-plans', currentEnv),
    queryFn: () => systemService.getSubscriptionPlans(currentEnv),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useRefreshSubscriptionPlanCache() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => systemService.refreshSubscriptionPlanCache(currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: buildSystemConfigKey('subscription-plans', currentEnv),
      })
    },
  })
}

export function useCreateSubscriptionPlan() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: Parameters<typeof systemService.createSubscriptionPlan>[0]) =>
      systemService.createSubscriptionPlan(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: buildSystemConfigKey('subscription-plans', currentEnv),
      })
    },
  })
}

export function useUpdateSubscriptionPlan() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: {
      planId: string
      updates: Parameters<typeof systemService.updateSubscriptionPlan>[1]
    }) => systemService.updateSubscriptionPlan(payload.planId, payload.updates, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: buildSystemConfigKey('subscription-plans', currentEnv),
      })
    },
  })
}

export function useCreateSubscriptionPlanTier() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: {
      planId: string
      tier: Parameters<typeof systemService.createSubscriptionPlanTier>[1]
    }) => systemService.createSubscriptionPlanTier(payload.planId, payload.tier, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: buildSystemConfigKey('subscription-plans', currentEnv),
      })
    },
  })
}

export function useUpdateSubscriptionPlanTier() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: {
      planId: string
      tierId: string
      updates: Parameters<typeof systemService.updateSubscriptionPlanTier>[2]
    }) => systemService.updateSubscriptionPlanTier(
      payload.planId,
      payload.tierId,
      payload.updates,
      currentEnv
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: buildSystemConfigKey('subscription-plans', currentEnv),
      })
    },
  })
}
